import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useParams } from "react-router-dom";
import { palette } from "../../../assets/styles/Palette";
import { getInfoBoardViewOnServer, getPlalionSensorData, getWeatherDataInDB, getInfoBoardContentsDetailOnServer } from "../../../utils/indexedDB";
import MoleculeBackgroundSimpleWide from "../../CreateEducationContentPage/components/molecules/MoleculeBackgroundSimpleWide";
import MoleculeBackgroundSimpleWideLink from "../../CreateEducationContentPage/components/molecules/MoleculeBackgroundSimpleWideLink";
import MoleculeMenuBoard from "../../CreateEducationContentPage/components/molecules/MoleculeMenuBoard";
import MoleculePlayFloating from "../../Global/components/molecules/MoleculePlayFloating";
import { AtomGetLogo } from "../../Global/components/atoms/AtomGetLogo";
import OrganismDisplayViewLinkLotationInfoBoard from "../components/organisms/OrganismDisplayViewLinkLotationInfoBoard";
import OrganismDisplayViewLinkLotationApiInfoBoard from "../components/organisms/OrganismDisplayViewLinkApiLotationInfoBoard";

// 통합 로테이션 페이지
// 경로 예시: /AutodisplayViewLinkLotation/:path
// - URL의 :path에서 viewId를 추출하여 API 호출
// - API 응답의 lotationContents 배열을 순환
// - 각 항목의 contentType 에 따라 분기:
//   - "general" | "meal": EduMobile 렌더링을 따름
//   - "sensor": API 뷰어(OrganismDisplayViewLinkLotation) 렌더링
const PageAutodisplayViewLinkLotation = () => {
  const { path, contentId } = useParams();
  const isPreviewMode = !!contentId; // contentId가 있으면 미리보기 모드
  const isImagePreview = path === "preview" && contentId === "image"; // 이미지 미리보기 모드 (path="preview", contentId="image"일 때)

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [lotationTimeSec, setLotationTimeSec] = useState(10); // 기본 10초
  const [lotationContents, setLotationContents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playing, setPlaying] = useState(!isPreviewMode); // 미리보기 모드에서는 자동 재생 비활성화
  const [scale, setScale] = useState(1);
  const [isLandscape, setIsLandscape] = useState(true);

  const timerRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const weatherRefreshTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const weatherLocationRef = useRef({ lat: null, lon: null });

  const [logo, setLogo] = useState(null);
  const [backgroundImage, setBackgroundImage] = useState(null);

  //! #########################################################################################
  const [headerType, setHeaderType] = useState("sensor"); //! 모던 펠리스 임시 추가입니다! ###########################################
  //! #########################################################################################

  // 뷰포트 기준 스케일 계산 (1920x1080 or 1080x1920 기준, Contain)
  const recalcScale = useCallback(() => {
    try {
      const vw = window.innerWidth || document.documentElement.clientWidth || 1920;
      const vh = window.innerHeight || document.documentElement.clientHeight || 1080;
      
      const landscape = vw >= vh;
      setIsLandscape(landscape);

      const targetW = landscape ? 1920 : 1080;
      const targetH = landscape ? 1080 : 1920;

      // 더 짧은 쪽 기준으로 비율 계산 (Contain)
      const nextScale = Math.min(vw / targetW, vh / targetH);
      setScale(nextScale);
    } catch {
      setScale(1);
    }
  }, []);

  // 페이지 레벨에서 플라리온 센서 데이터를 가져오는 함수
  const fetchPlalionSensorForPage = useCallback(async (serial) => {
    if (!serial || typeof getPlalionSensorData !== "function") return;
    try {
      const response = await getPlalionSensorData({ serial_num: serial });
      if (response && response.code === 101 && Array.isArray(response.rows) && response.rows.length > 0) {
        const row = response.rows[0];
        setSensorData({
          ozone: row.ozone_val ? parseFloat(row.ozone_val).toFixed(3) : "-",
          temperature: row.temp_val ? parseFloat(row.temp_val) : "-",
          humidity: row.humi_val ? parseFloat(row.humi_val) : "-",
          dust: row.dust_val ? parseFloat(row.dust_val) : "-",
          voc: row.voc_val ? parseFloat(row.voc_val) : "-",
          co2: row.co2_val ? parseFloat(row.co2_val) : "-",
        });
      } else {
        setSensorData({ ozone: "-", temperature: "-", humidity: "-", dust: "-", voc: "-", co2: "-" });
      }
    } catch (err) {
      console.error("fetchPlalionSensorForPage failed:", err);
      setSensorData({ ozone: "-", temperature: "-", humidity: "-", dust: "-", voc: "-", co2: "-" });
    }
  }, []);

  useEffect(() => {
    recalcScale();
    window.addEventListener("resize", recalcScale);
    window.addEventListener("orientationchange", recalcScale);
    return () => {
      window.removeEventListener("resize", recalcScale);
      window.removeEventListener("orientationchange", recalcScale);
    };
  }, [recalcScale]);

  // 날씨 상태
  const [weatherData, setWeatherData] = useState({
    temperature: null,
    wind: null,
    humidity: null,
    precipitation: null,
    minTemperature: null,
    maxTemperature: null,
    weatherMain: null,
    weatherIcon: null,
    sky: null,
    pty: null,
    icon: null,
  });
  // 페이지 레벨에서 관리하는 센서 데이터 (1분마다 페이지가 수집하여 전달)
  const [sensorData, setSensorData] = useState({
    ozone: "-",
    temperature: "-",
    humidity: "-",
    dust: "-",
    voc: "-",
    co2: "-",
  });
  const sensorTimerRef = useRef(null);

  // 페이지 공용 날씨 로딩 (OpenWeather 우선, lat/lon 사용, 없으면 city 키워드)
  const fetchWeatherForPage = useCallback(async () => {
    try {
      // 1) 페이지 수준으로 추출한 위치(lat/lon) 우선 사용
      const { x, y } = weatherLocationRef.current || {};

      if (x && y && typeof getWeatherDataInDB === "function") {
        const res = await getWeatherDataInDB({ x, y });
        const data = res?.data?.data;
        // console.log("fetchWeatherForPage - WeatherDataInDB by x/y:", data);
        if (data) {
          setWeatherData((prev) => ({
            ...prev,
            temperature: Math.round(data.temp),
            minTemperature: Math.round(data.temp_min),
            maxTemperature: Math.round(data.temp_max),
            wind: data.wsd,
            humidity: Math.round(data.humidity),
            precipitation: data.rain_1h,
            weatherMain: data.weather_main,
            weatherIcon: data.weather_icon,
            icon: data.weather_icon,
          }));
          return;
        }
      }
    } catch (e) {
      // 조용히 실패 처리 (하위 컴포넌트가 자체 fallback 가능)
      // console.warn("fetchWeatherForPage failed:", e);
    }
  }, []);

  // lotationContents에서 위치(lat/lon) 한 번만 추출해 보관 (콘텐츠 변경마다 API 호출하지 않음)
  useEffect(() => {
    let x = null;
    let y = null;
    for (const item of lotationContents || []) {
      const districts = item?.contentData?.selectedDistricts;
      if (districts?.x && districts?.y) {
        x = districts.x;
        y = districts.y;
        break;
      }
    }
    weatherLocationRef.current = { x, y };
    // 좌표가 생기면 즉시 페이지 날씨 갱신
    if (x && y) {
      fetchWeatherForPage();
    }
  }, [lotationContents]);

  // 날씨 정각 50분마다 갱신
  useEffect(() => {
    if (weatherRefreshTimerRef.current) {
      clearInterval(weatherRefreshTimerRef.current);
      weatherRefreshTimerRef.current = null;
    }
    weatherRefreshTimerRef.current = setInterval(() => {
      const now = new Date();
      if (now.getMinutes() === 50) {
        fetchWeatherForPage();
      }
    }, 60 * 1000); // 1분마다 체크

    return () => {
      if (weatherRefreshTimerRef.current) {
        clearInterval(weatherRefreshTimerRef.current);
        weatherRefreshTimerRef.current = null;
      }
    };
  }, [fetchWeatherForPage]);

  // 전역 스크롤바 숨김 처리 (GlobalStyle override)
  useEffect(() => {
    // 마운트 시 html, body overflow hidden 처리
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      // 언마운트 시 복구 (GlobalStyle의 기본값으로 되돌림 - 보통 scroll이나 auto)
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    };
  }, []);

  // 데이터 로드
  const fetchLotation = useCallback(
    async (options = { silent: false }) => {
      
      // 이미지 미리보기가 아닌 경우 path 필수
      if (!isImagePreview && !path) return;
      try {
        if (!options.silent) {
          setIsLoading(true);
          setError("");
        }

        // 이미지 미리보기 모드: sessionStorage에서 데이터 로드
        if (isImagePreview) {
          try {
            const imagePreviewData = sessionStorage.getItem('imagePreviewData');
            if (!imagePreviewData) {
              throw new Error("미리보기 데이터를 찾을 수 없습니다.");
            }

            const previewData = JSON.parse(imagePreviewData);

            console.log("Loaded image preview data from sessionStorage:", previewData);
            
            if (!isMountedRef.current) return;

            // 이미지 미리보기용 콘텐츠 구조 생성
            const previewContent = {
              contentsType: "image",
              viewId: "preview_image",
              contentData: {
                title: previewData.title,
                imageUrl: previewData.imageUrl,
                fileName: previewData.fileName
              }
            };

            setLotationContents([previewContent]);
            setCurrentIndex(0);
            setLotationTimeSec(10);
            
            // 기본 로고 설정
            const [logo, backgroundImage] = AtomGetLogo(null);
            setLogo(logo);
            setBackgroundImage(backgroundImage);

            if (!options.silent) setIsLoading(false);
            console.log("이미지 미리보기 데이터 로드 성공:", previewContent);
            return;
          } catch (e) {
            console.error("이미지 미리보기 로드 실패:", e);
            if (!isMountedRef.current) return;
            setError("이미지 미리보기를 불러올 수 없습니다.");
            setIsLoading(false);
            return;
          }
        }

        // 미리보기 모드: contentId로 단일 콘텐츠 직접 로드
        if (isPreviewMode && contentId) {
          try {
            const contentResponse = await getInfoBoardContentsDetailOnServer(contentId);
            const previewContent = contentResponse.data || contentResponse;
            
            if (!previewContent) {
              throw new Error("미리보기 콘텐츠를 찾을 수 없습니다.");
            }

            if (!isMountedRef.current) return;

            // 단일 콘텐츠를 배열로 감싸기
            setLotationContents([previewContent]);
            setCurrentIndex(0);
            setLotationTimeSec(10);
            
            // 조직 정보 로드
            const organization_type = previewContent?.organizationType || null;
            const [logo, backgroundImage] = AtomGetLogo(organization_type);
            setLogo(logo);
            setBackgroundImage(backgroundImage);

            // 좌표 추출 및 날씨 로드
            try {
              const districts = previewContent?.contentData?.selectedDistricts;
              if (districts?.x && districts?.y) {
                weatherLocationRef.current = { x: districts.x, y: districts.y };
                await fetchWeatherForPage();
              }
            } catch (wErr) {
              // 날씨 실패는 콘텐츠 로딩을 막지 않음
            }

            if (!options.silent) setIsLoading(false);
            return;
          } catch (e) {
            console.error("미리보기 콘텐츠 로드 실패:", e);
            if (!isMountedRef.current) return;
            setError("미리보기 콘텐츠를 불러올 수 없습니다.");
            setIsLoading(false);
            return;
          }
        }

        // 일반 모드 - path가 필수이어야 함
        if (!path) {
          setError("유효한 경로가 없습니다.");
          setIsLoading(false);
          return;
        }

        // 일반 모드: 전체 편성 데이터 로드
        const response = await getInfoBoardViewOnServer(path);
        // response.data가 배열인 경우(새 JSON 구조)와 기존 구조 호환
        const table = response?.lotationTable || response;
  
        let contents = [];
        if (Array.isArray(response?.data)) {
          contents = response.data;
        } else {
          contents = Array.isArray(table?.lotationContents) ? table.lotationContents : [];
        }

        // [0]의 zones와 일치하는 항목만 필터링
        if (contents.length > 0 && contents[0]?.zones && Array.isArray(contents[0].zones)) {
          const firstItemZones = contents[0].zones;
          contents = contents.filter(item => {
            // item이 zones를 가지고 있고, firstItemZones의 모든 요소가 item.zones에 포함되는지 확인
            if (item?.zones && Array.isArray(item.zones)) {
              return firstItemZones.every(zone => item.zones.includes(zone));
            }
            return false; // zones가 없는 항목은 제외
          });
        }

        if (!isMountedRef.current) return;

        const timeVal = parseInt(contents[0]?.lotationTime || 10); // 기본 10초
        setLotationTimeSec(Number.isFinite(timeVal) ? timeVal : 10);

        // dataType 기반으로 센서/날씨 패널 구분
        const firstContentDataType = contents[0]?.contentData?.dataType || "sensor";
        const organization_type = contents[0]?.organizationType || null;

        let filteredContents = contents;
        let initialIndex = 0;
        // dataType이 "public" (공공데이터전용)일 때 날씨 패널 표시
        if (firstContentDataType === "public") {
          setHeaderType("weather");
          // 첫 번째 콘텐츠는 데이터에는 남기고, 화면에선 두 번째부터 보여줌
          initialIndex = 1;
        }
        const [logo, backgroundImage] = AtomGetLogo(organization_type);
        setLogo(logo);
        setBackgroundImage(backgroundImage);

        // bottomText는 더 이상 사용하지 않음 - apiViewerProps에서 동적으로 계산됨
            // DEV: For testing, show only general contents and disable auto rotation.
            // To revert, set devShowGeneralOnly to false
            const devShowGeneralOnly = false;
            let finalContents = filteredContents;
            if (devShowGeneralOnly) {
              const generalOnly = filteredContents.filter((c) => c.contentsType === "meal");
              if (generalOnly.length > 0) {
                finalContents = generalOnly;
                // Stop playing so the content does not rotate during testing
                setPlaying(false);
              }
            }

            // 식단 콘텐츠 중 showWeeklyView가 true인 경우, 
            // [일일 식단, 주간 식단] 두 개로 분리하여 배열에 추가
            const processedContents = finalContents.flatMap((item) => {
              // 식단이면서 주간 보기 옵션이 켜져 있는 경우
              if (item.contentsType === "meal" && item.showWeeklyView) {
                // 1. 일일 식단 (showWeeklyView: false로 강제)
                const dailyItem = {
                  ...item,
                  showWeeklyView: false,
                  _generatedId: `${item.viewId || 'meal'}_daily` // 키 충돌 방지용 임시 ID
                };
                
                // 2. 주간 식단 (showWeeklyView: true 유지)
                const weeklyItem = {
                  ...item,
                  // 이미 true지만 명시적으로 확인
                  showWeeklyView: true,
                  _generatedId: `${item.viewId || 'meal'}_weekly`
                };
                
                return [dailyItem, weeklyItem];
              }
              // 그 외(일반 콘텐츠, 센서 등)는 그대로 반환
              return [item];
            });

            setLotationContents(processedContents);
            setCurrentIndex(initialIndex);

        // 최초 로드에서 바로 좌표 추출 후 날씨를 먼저 불러와 로딩 해제 이전에 weatherData 채움
        try {
          let lat = null;
          let lon = null;
          for (const item of contents || []) {
            const districts = item?.contentData?.selectedDistricts;
            if (districts?.lat && districts?.lon) {
              lat = districts.lat;
              lon = districts.lon;
              break;
            }
          }
          weatherLocationRef.current = { lat, lon };
          if (lat && lon) {
            await fetchWeatherForPage();
          } else {
            // 좌표가 없더라도 환경변수 기반 OpenWeather fallback 시도
            await fetchWeatherForPage();
          }
        } catch (wErr) {
          // 날씨 실패는 페이지 로딩을 막지 않음
          // console.warn("initial weather fetch failed:", wErr);
        }
      } catch (e) {
        console.error(e);
        if (!isMountedRef.current) return;
        setError("데이터 로드 중 오류가 발생했습니다.");
      } finally {
        if (!isMountedRef.current) return;
        if (!options.silent) setIsLoading(false);
      }
    },
    [path, contentId, isPreviewMode, isImagePreview, fetchWeatherForPage]
  );

  // 마운트/언마운트 관리 및 최초 로드
  useEffect(() => {
    isMountedRef.current = true;
    fetchLotation({ silent: false });
    return () => {
      isMountedRef.current = false;
    };
  }, [fetchLotation]);

  // 10분 간격 데이터 새로고침 (이미지 미리보기 모드에서는 스킵)
  useEffect(() => {
    if (!path || isImagePreview) return;
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
    }
    refreshTimerRef.current = setInterval(() => {
      fetchLotation({ silent: true });
    }, 10 * 60 * 1000);
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      if (weatherRefreshTimerRef.current) {
        clearInterval(weatherRefreshTimerRef.current);
        weatherRefreshTimerRef.current = null;
      }
    };
  }, [path, fetchLotation]);

  // 현재 표시할 콘텐츠
  const currentContent = useMemo(() => {
    if (!lotationContents.length) return null;
    return lotationContents[currentIndex] || null;
  }, [lotationContents, currentIndex]);

  // default sensor: find the first sensor content in lotationContents (fallback for non-sensor content)
  // public 타입일 때: 첫 번째 콘텐츠에서 textData 가져오기
  const defaultSensor = useMemo(() => {
    if (!lotationContents || !lotationContents.length) return null;
    
    // public 타입일 때는 첫 번째 콘텐츠에서 textData 추출
    const firstContent = lotationContents[0];
    if (firstContent?.contentData?.dataType === "public") {
      return {
        title: firstContent?.contentData?.title || "",
        serialCode: "",
        selectedDistricts: firstContent?.contentData?.selectedDistricts || null,
        selectedRegion: firstContent?.contentData?.selectedRegion || null,
        textData: firstContent?.textData || ""
      };
    }
    
    // 기존 로직: sensor 타입 찾기
    const found = lotationContents.find((c) => c.contentsType === "sensor" && (c.contentData?.serialCode || c.contentData?.serial));
    if (!found) return null;
    const contentData = found.contentData || {};
    const sensorTextData =
      found?.selectedMessage?.text ||
      found?.textData ||
      contentData?.textData || "";
    return {
      title: contentData.title || found.title || "",
      serialCode: contentData.serialCode || contentData.serial || "",
      selectedDistricts: contentData.selectedDistricts || null,
      selectedRegion: contentData.selectedRegion || null,
      textData: sensorTextData,
    };
  }, [lotationContents]);

  // 현재 콘텐츠 기반으로 타이머 설정 (재생 상태/타입 반영)
  useEffect(() => {
    if (!lotationContents.length) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    // 미리보기 모드에서는 타이머 작동하지 않음
    if (!playing || isPreviewMode) return;

    // 주간 식단표는 20초 고정
    let contentTime;
    if (currentContent?.contentsType === "meal" && currentContent?.showWeeklyView) {
      contentTime = 20; // 주간 식단표는 20초 고정
    } else if (currentContent && parseInt(currentContent.lotationTime, 10) > 0) {
      contentTime = parseInt(currentContent.lotationTime, 10);
    } else {
      contentTime = lotationTimeSec;
    }

    const intervalMs = (Math.max(1, contentTime)) * 1000;

    timerRef.current = setInterval(() => {
      setCurrentIndex((prev) => {
        // dataType이 "public"일 때 0번째 인덱스는 건너뛰기
        if (lotationContents.length === 0) return 0;
        const firstContentDataType = lotationContents[0]?.contentData?.dataType || "sensor";
        if (firstContentDataType === "public") {
          // 마지막 인덱스면 다시 1로, 아니면 +1
          if (prev + 1 >= lotationContents.length) return 1;
          if (prev + 1 === 0) return 1;
          return prev + 1;
        }
        return (prev + 1) % lotationContents.length;
      });
    }, intervalMs);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [lotationContents, lotationTimeSec, currentContent, playing]);

  // 플로팅 컨트롤 #######################################################################
  const handleTogglePlay = useCallback(() => {
    setPlaying((prev) => !prev);
  }, []);

  const handlePrev = useCallback(() => {
    if (!lotationContents.length) return;
    setCurrentIndex((prev) => {
      const firstContentDataType = lotationContents[0]?.contentData?.dataType || "sensor";
      if (firstContentDataType === "public") {
        // 1 미만이면 마지막 인덱스로, 아니면 -1
        return prev > 1 ? prev - 1 : lotationContents.length - 1;
      }
      return prev > 0 ? prev - 1 : Math.max(0, lotationContents.length - 1);
    });
  }, [lotationContents]);

  const handleNext = useCallback(() => {
    if (!lotationContents.length) return;
    setCurrentIndex((prev) => {
      const firstContentDataType = lotationContents[0]?.contentData?.dataType || "sensor";
      if (firstContentDataType === "public") {
        // 마지막 인덱스면 다시 1로, 아니면 +1
        if (prev + 1 >= lotationContents.length) return 1;
        if (prev + 1 === 0) return 1;
        return prev + 1;
      }
      return (prev + 1) % lotationContents.length;
    });
  }, [lotationContents]);
  // #########################################################################################

  // API 뷰어용(팩토리보드) 데이터 변환 (sensor 타입 분기) ######################################
  const apiViewerProps = useMemo(() => {
    if (!currentContent) {
      return {
        viewId: path,
        textData: "",
        sensorSerialData: null,
        locationData: null,
        themeMode: "light",
      };
    }
    const contentData = currentContent?.contentData || {};
    const sensor = {
      title: contentData.title || defaultSensor?.title || "",
      serialCode: contentData.serialCode || contentData.serial || defaultSensor?.serialCode || "",
    }
    const location =
      contentData.selectedRegion || contentData.selectedDistricts
        ? {
            selectedRegion: contentData.selectedRegion,
            selectedDistricts: contentData.selectedDistricts,
          }
        : defaultSensor && (defaultSensor.selectedRegion || defaultSensor.selectedDistricts)
        ? {
            selectedRegion: defaultSensor.selectedRegion,
            selectedDistricts: defaultSensor.selectedDistricts,
          }
        : null;
    const concept = currentContent.designConcept || currentContent.designSelectedConcept || [];
    const conceptFirst = Array.isArray(concept) ? concept[0] : concept;
    const mode = conceptFirst === "다크 모드" || conceptFirst === "dark" ? "dark" : "light";
    // 동적으로 textData 계산: selectedMessage → currentContent → defaultSensor 순서
    const textData = currentContent?.selectedMessage?.text || currentContent?.textData || defaultSensor?.textData || "";
    console.log("API Viewer Props:", {
      viewId: currentContent.viewId || path,
      textData: textData,
      sensorSerialData: sensor,
      locationData: location,
      themeMode: mode,
    });
    return {
      viewId: currentContent.viewId || path,
      textData: textData,
      sensorSerialData: sensor,
      locationData: location,
      themeMode: mode,
    };
  }, [currentContent, path, defaultSensor]);

  // 페이지 레벨: 현재 보이는 센서의 serial 변경 시 1분 간격으로 센서 데이터 갱신
  useEffect(() => {
    const serial = apiViewerProps?.sensorSerialData?.serialCode || apiViewerProps?.sensorSerialData?.serial;
    if (!serial) {
      if (sensorTimerRef.current) {
        clearInterval(sensorTimerRef.current);
        sensorTimerRef.current = null;
      }
      setSensorData({ ozone: "-", temperature: "-", humidity: "-", dust: "-", voc: "-", co2: "-" });
      return;
    }
    // 초기 한 번 가져옴
    fetchPlalionSensorForPage(serial);

    if (sensorTimerRef.current) {
      clearInterval(sensorTimerRef.current);
    }
    sensorTimerRef.current = setInterval(() => {
      fetchPlalionSensorForPage(serial);
    }, 60 * 1000); // 1 minute

    return () => {
      if (sensorTimerRef.current) {
        clearInterval(sensorTimerRef.current);
        sensorTimerRef.current = null;
      }
    };
  }, [apiViewerProps?.sensorSerialData?.serialCode, apiViewerProps?.sensorSerialData?.serial, fetchPlalionSensorForPage]);
  // #########################################################################################

  if (isLoading) {
    return (
      <FullScreenContainer>
        <LoadingText>로딩 중...</LoadingText>
      </FullScreenContainer>
    );
  }

  if (error) {
    return (
      <FullScreenContainer>
        <ErrorText>{error}</ErrorText>
      </FullScreenContainer>
    );
  }

  if (!path) {
    return null;
  }

  if (!currentContent) {
    return (
      <FullScreenContainer>
        <EmptyText>편성된 콘텐츠가 없습니다.</EmptyText>
      </FullScreenContainer>
    );
  }

  // 원본 타입 비교
  const typeRaw = currentContent?.contentsType;
  const isSensor = typeRaw === "sensor";
  const isMeal = typeRaw === "meal";
  const isLink = typeRaw === "link";
  const isImage = typeRaw === "image";

  // EduMobile 렌더링에 필요한 필드 추출
  const designConcept = currentContent?.designConcept || "";
  const title = isImage
    ? currentContent?.dataAnalysis?.title || currentContent?.contentData?.title || ""
    : currentContent?.contentGeneratorTitle || "";
  const markdown = currentContent?.contentGeneratorMarkdown || "";
  const mealData = currentContent?.contentGeneratorMeal || {};
  const showWeeklyView = currentContent?.showWeeklyView || false;
  const nutritionMessage = currentContent?.nutritionMessage || "";
  const linkUrl = currentContent?.linkUrl || "";
  const imageUrl = isImage && currentContent?.contentData?.imageUrl
    ? currentContent.contentData.imageUrl
    : currentContent?.uploaded_images?.[0]?.url || "";

  return (
    <>
      <GlobalBackground image={backgroundImage} />
      <FullScreenContainer scale={scale} isLandscape={isLandscape}>
        <AnimationWrapper key={currentIndex}>
          {isSensor ? (
            <SensorWrapper>
              <OrganismDisplayViewLinkLotationApiInfoBoard
                viewId={apiViewerProps.viewId}
                textData={apiViewerProps.textData}
                sensorSerialData={apiViewerProps.sensorSerialData}
                locationData={apiViewerProps.locationData}
                themeMode={apiViewerProps.themeMode}
                weatherData={weatherData}
                sensorData={sensorData}
                backgroundImage= "none"
              />
            </SensorWrapper>
          ) : (
            <SensorWrapper>
              <OrganismDisplayViewLinkLotationInfoBoard
                viewId={apiViewerProps.viewId}
                textData={apiViewerProps.textData}
                sensorSerialData={apiViewerProps.sensorSerialData}
                locationData={apiViewerProps.locationData}
                themeMode={apiViewerProps.themeMode}
                weatherData={weatherData}
                sensorData={sensorData}
                backgroundImage="none"
                logo={logo}
                contentsDesignConcept={designConcept}
                onTogglePlay={handleTogglePlay}

                // 식단 데이터
                isMeal={isMeal}
                menuData={mealData}
                noticeText={nutritionMessage}
                showWeeklyView={showWeeklyView}

                // 일반 콘텐츠 데이터
                title={title}
                content={markdown}

                // 링크 콘텐츠 데이터
                isLink={isLink}
                linkUrl={linkUrl}

                // 이미지 콘텐츠 데이터
                isImage={isImage}
                imageUrl={imageUrl}

                // 헤더 타입
                headerType={headerType}
              />
            </SensorWrapper>
          )}
        </AnimationWrapper>

        {false && (
          <MoleculePlayFloating //! 플로팅 컨트롤러 비활성화 중
            isPlaying={playing}
            onTogglePlay={handleTogglePlay}
            onPrev={handlePrev}
            onNext={handleNext}
            floating={true}
            zIndex={9999}
            style={{
              position: "fixed",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          />
        )}
      </FullScreenContainer>
    </>
  );
};

export default PageAutodisplayViewLinkLotation;

// 애니메이션
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const AnimationWrapper = styled.div`
  width: 100%;
  height: 100%;
  animation: ${slideIn} 0.5s ease-out;
`;

const GlobalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: ${({ image }) => (image ? `url(${image})` : "transparent")};
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  z-index: -1;
`;

// 공통 컨테이너
const FullScreenContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  /* 스케일링 및 중앙 정렬 (순서 중요: translate 먼저, 그 다음 scale) */
  transform: translate(-50%, -50%) scale(${props => props.scale});
  transform-origin: center center;

  /* 가로/세로 모드에 따른 고정 해상도 */
  width: ${props => props.isLandscape ? '1920px' : '1080px'};
  height: ${props => props.isLandscape ? '1080px' : '1920px'};

  margin: 0;
  padding: 0;
  background: transparent;
  overflow: hidden;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  /* 스크롤바 숨김 */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const LoadingText = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: ${palette.gray200};
`;

const ErrorText = styled(LoadingText)`
  color: ${palette.error};
`;

const EmptyText = styled(LoadingText)`
  color: ${palette.gray300};
`;

// EduMobile 스타일
const ComponentWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  margin: 0 auto;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ComponentContainer = styled.div`
  width: 3840px;
  height: 2160px;
  background: white;
  position: relative;
  overflow: visible;
  transform-origin: center center;
  will-change: transform;
`;

// Sensor(API 뷰어) 전용 래퍼
const SensorWrapper = styled.div`
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  background: transparent;
  overflow: hidden;
  position: relative;
  
  /* OrganismDisplayViewLinkLotationInfoBoard 내부의 OuterWrapper 강제 스타일링 */
  /* 부모인 FullScreenContainer 크기에 딱 맞추기 위해 100vw/100vh를 무시하고 100%로 설정 */
  & > div {
    width: 100% !important;
    height: 100% !important;
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    background: transparent !important; /* Page 배경색 사용 */
  }
`;


